import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  GraduationCap, BookOpen, Award, Clock, ChevronRight, 
  CheckCircle, Users, Star, Truck, Headphones, Briefcase, 
  Building2, Play, Lock, ArrowRight, Sparkles, Shield,
  Calculator, MessageCircle, Film, Rocket
} from 'lucide-react';
import { ACADEMY_INFO, COURSES, BUNDLE_INFO, HOW_IT_WORKS, TESTIMONIALS, FAQ } from '../courseData';

// Icon mapping for dynamic rendering
const ICON_MAP = {
  Truck,
  Headphones,
  Briefcase,
  Building2
};

const QueenBAcademyPage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const handlePurchase = (type, courseId = null) => {
    // Navigate to Academy checkout
    navigate('/academy/checkout', { 
      state: { 
        purchaseType: type, 
        courseId 
      } 
    });
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="academy-page">
      <Helmet>
        <title>Queen B Academy | Premium Trucking Education</title>
        <meta name="description" content="Transform your trucking career with Queen B Academy. CDL training, dispatcher courses, business fundamentals, and freight brokerage education." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 via-transparent to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-8" data-testid="academy-badge">
              <GraduationCap className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wider">Queen B Academy</span>
            </div>

            {/* Main headline */}
            <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase leading-tight mb-6">
              Education Built for{' '}
              <span className="text-[#D4AF37]">The Road Ahead</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-10">
              Premium trucking education designed to transform beginners into industry professionals. 
              Master your craft with comprehensive courses built by experts who've driven the miles.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => document.getElementById('courses').scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8962E] transition-colors"
                data-testid="explore-courses-btn"
              >
                <BookOpen className="w-5 h-5" />
                Explore Courses
              </button>
              <button
                onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border-2 border-white/20 text-white font-bold uppercase tracking-wider hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                data-testid="view-pricing-btn"
              >
                <Sparkles className="w-5 h-5" />
                View Bundle Deal
              </button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-8 mt-12 text-white/40">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span>No placeholders</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#D4AF37]" />
                <span>Self-paced learning</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-[#C0C0C0]" />
                <span>Industry expertise</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-4">Our Curriculum</p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
              Four Pillars of{' '}
              <span className="text-[#D4AF37]">Trucking Mastery</span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Each course is meticulously crafted with real-world insights, actionable strategies, and complete lesson content.
            </p>
          </div>

          {/* Course Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {COURSES.map((course) => {
              const IconComponent = ICON_MAP[course.icon] || BookOpen;
              return (
                <div
                  key={course.id}
                  className="group bg-[#121212] border border-white/5 hover:border-white/10 transition-all duration-300 overflow-hidden"
                  data-testid={`course-card-${course.id}`}
                >
                  {/* Course Header */}
                  <div className="p-6 border-b border-white/5" style={{ borderLeftWidth: 4, borderLeftColor: course.color }}>
                    <div className="flex items-start justify-between mb-4">
                      <div 
                        className="p-3"
                        style={{ backgroundColor: `${course.color}15` }}
                      >
                        <IconComponent className="w-6 h-6" style={{ color: course.color }} />
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-white">${course.price}</span>
                        <p className="text-white/40 text-sm">one-time</p>
                      </div>
                    </div>
                    
                    <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase mb-2">
                      {course.title}
                    </h3>
                    <p className="text-white/40 text-sm mb-4">{course.subtitle}</p>
                    
                    <div className="flex flex-wrap gap-3 text-xs">
                      <span className="px-2 py-1 bg-white/5 text-white/60">{course.duration}</span>
                      <span className="px-2 py-1 bg-white/5 text-white/60">{course.level}</span>
                      <span className="px-2 py-1 text-white/60" style={{ backgroundColor: `${course.color}15`, color: course.color }}>
                        {course.category}
                      </span>
                    </div>
                  </div>

                  {/* Course Content */}
                  <div className="p-6">
                    <p className="text-white/60 text-sm mb-6 line-clamp-3">{course.description}</p>
                    
                    {/* Features */}
                    <div className="space-y-2 mb-6">
                      {course.features.slice(0, 4).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-white/70">{feature}</span>
                        </div>
                      ))}
                      {course.features.length > 4 && (
                        <p className="text-white/40 text-xs pl-6">+{course.features.length - 4} more features</p>
                      )}
                    </div>

                    {/* Modules Preview */}
                    <div className="bg-[#0A0A0A] p-4 mb-6">
                      <p className="text-white/40 text-xs uppercase tracking-wider mb-3">{course.modules.length} Modules</p>
                      <div className="space-y-2">
                        {course.modules.slice(0, 3).map((module, idx) => (
                          <div key={module.id} className="flex items-center gap-2 text-sm">
                            <span className="text-[#D4AF37] font-mono text-xs">{String(idx + 1).padStart(2, '0')}</span>
                            <span className="text-white/70">{module.title}</span>
                          </div>
                        ))}
                        {course.modules.length > 3 && (
                          <p className="text-white/40 text-xs">+{course.modules.length - 3} more modules</p>
                        )}
                      </div>
                    </div>

                    {/* CTA */}
                    <button
                      onClick={() => setSelectedCourse(course)}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-white/5 border border-white/10 text-white font-medium hover:bg-white/10 hover:border-white/20 transition-colors"
                      data-testid={`view-course-${course.id}`}
                    >
                      View Full Curriculum
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Premium Features Section - NEW */}
      <section className="py-20 bg-[#0A0A0A]" data-testid="premium-features">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-4">Premium Features</p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
              More Than Just{' '}
              <span className="text-[#D4AF37]">Video Lessons</span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Queen B Academy includes powerful tools, a thriving community, and AI-powered features to accelerate your success.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Interactive Calculators */}
            <div className="bg-[#121212] border border-white/5 hover:border-white/10 transition-all p-6 group">
              <div className="p-3 bg-[#D4AF37]/10 w-fit mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                <Calculator className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase mb-2">
                Business Calculators
              </h3>
              <p className="text-white/40 text-sm mb-4">
                Plan your trucking business with precision using our interactive tools.
              </p>
              <ul className="space-y-2 text-xs text-white/50">
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#D4AF37]" />Startup Cost Calculator</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#D4AF37]" />Revenue & Profit Calculator</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#D4AF37]" />Break-Even Analysis</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#D4AF37]" />Rate Negotiation Simulator</li>
              </ul>
            </div>

            {/* Community Forum */}
            <div className="bg-[#121212] border border-white/5 hover:border-white/10 transition-all p-6 group">
              <div className="p-3 bg-[#D4AF37]/10 w-fit mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                <MessageCircle className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase mb-2">
                Student Community
              </h3>
              <p className="text-white/40 text-sm mb-4">
                Connect with fellow students, ask questions, and share your success stories.
              </p>
              <ul className="space-y-2 text-xs text-white/50">
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#D4AF37]" />Discussion Forums</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#D4AF37]" />Q&A with Expert Answers</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#D4AF37]" />Resource Sharing</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#D4AF37]" />Success Stories</li>
              </ul>
            </div>

            {/* AI Videos */}
            <div className="bg-[#121212] border border-white/5 hover:border-white/10 transition-all p-6 group relative overflow-hidden">
              <div className="absolute top-2 right-2 px-2 py-0.5 bg-purple-500/20 border border-purple-500/30 text-purple-400 text-[10px] font-bold uppercase">
                AI Powered
              </div>
              <div className="p-3 bg-purple-500/10 w-fit mb-6 group-hover:bg-purple-500/20 transition-colors">
                <Film className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase mb-2">
                AI Course Videos
              </h3>
              <p className="text-white/40 text-sm mb-4">
                Unique AI-generated video intros and content powered by Sora 2.
              </p>
              <ul className="space-y-2 text-xs text-white/50">
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-purple-400" />Course Intro Videos</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-purple-400" />Progress Tracking</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-purple-400" />HD Quality</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-purple-400" />Unique to Your Experience</li>
              </ul>
            </div>

            {/* Entrepreneurship Pipeline */}
            <div className="bg-[#121212] border border-white/5 hover:border-white/10 transition-all p-6 group">
              <div className="p-3 bg-[#D4AF37]/10 w-fit mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                <Rocket className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase mb-2">
                Start Your Business
              </h3>
              <p className="text-white/40 text-sm mb-4">
                Go from student to business owner with our BUILD integration.
              </p>
              <ul className="space-y-2 text-xs text-white/50">
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#D4AF37]" />5-Step Business Roadmap</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#D4AF37]" />Career Path Guidance</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#D4AF37]" />BUILD Package Access</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-[#D4AF37]" />Authority Launch Support</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-4">Investment</p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
              Choose Your{' '}
              <span className="text-[#D4AF37]">Path</span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Individual courses or the complete bundle - education that pays for itself.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Individual Courses */}
            <div className="bg-[#0A0A0A] border border-white/10 p-8" data-testid="individual-pricing">
              <div className="text-center mb-8">
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase mb-2">Individual Courses</h3>
                <p className="text-white/50 text-sm">Select specific courses for your needs</p>
              </div>

              <div className="space-y-4 mb-8">
                {COURSES.map((course) => {
                  const IconComponent = ICON_MAP[course.icon] || BookOpen;
                  return (
                    <div key={course.id} className="flex items-center justify-between p-4 bg-[#121212] border border-white/5">
                      <div className="flex items-center gap-3">
                        <IconComponent className="w-5 h-5" style={{ color: course.color }} />
                        <span className="text-white">{course.title}</span>
                      </div>
                      <span className="text-[#D4AF37] font-bold">${course.price}</span>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => handlePurchase('individual')}
                className="w-full py-4 bg-white/10 border border-white/20 text-white font-bold uppercase tracking-wider hover:bg-white/20 transition-colors"
                data-testid="purchase-individual-btn"
              >
                Select Individual Course
              </button>
            </div>

            {/* Bundle */}
            <div className="bg-[#121212] border-2 border-[#D4AF37] p-8 relative" data-testid="bundle-pricing">
              {/* Best Value Badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-black font-bold text-sm uppercase">
                Best Value
              </div>

              <div className="text-center mb-8 pt-4">
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase mb-2">{BUNDLE_INFO.name}</h3>
                <p className="text-white/50 text-sm mb-4">{BUNDLE_INFO.description}</p>
                
                <div className="flex items-center justify-center gap-3">
                  <span className="text-white/40 line-through text-lg">$1,000</span>
                  <span className="text-4xl font-bold text-[#D4AF37]">${BUNDLE_INFO.price}</span>
                </div>
                <p className="text-green-400 text-sm mt-2">Save ${BUNDLE_INFO.savings}</p>
              </div>

              <div className="space-y-3 mb-8">
                {COURSES.map((course) => {
                  const IconComponent = ICON_MAP[course.icon] || BookOpen;
                  return (
                    <div key={course.id} className="flex items-center gap-3 text-white/70">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <IconComponent className="w-4 h-4" style={{ color: course.color }} />
                      <span>{course.title}</span>
                    </div>
                  );
                })}
              </div>

              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 mb-8">
                <div className="flex items-center gap-2 text-[#D4AF37] mb-2">
                  <Award className="w-5 h-5" />
                  <span className="font-bold">Bundle Benefits</span>
                </div>
                <ul className="space-y-1 text-sm text-white/60">
                  <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-green-500" />Lifetime access to all 4 courses</li>
                  <li className="flex items-center gap-2"><Calculator className="w-3 h-3 text-[#D4AF37]" />Interactive business calculators</li>
                  <li className="flex items-center gap-2"><MessageCircle className="w-3 h-3 text-[#D4AF37]" />Student community forum access</li>
                  <li className="flex items-center gap-2"><Film className="w-3 h-3 text-purple-400" />AI-generated course videos</li>
                  <li className="flex items-center gap-2"><Rocket className="w-3 h-3 text-[#D4AF37]" />Entrepreneurship roadmap</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-green-500" />Priority support</li>
                </ul>
              </div>

              <button
                onClick={() => handlePurchase('bundle')}
                className="w-full py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8962E] transition-colors"
                data-testid="purchase-bundle-btn"
              >
                Get the Complete Bundle
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-4">Process</p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase">
              How It{' '}
              <span className="text-[#D4AF37]">Works</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {HOW_IT_WORKS.map((step, idx) => (
              <div key={step.step} className="relative" data-testid={`step-${step.step}`}>
                {/* Connector line */}
                {idx < HOW_IT_WORKS.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-1/2 w-full h-px bg-gradient-to-r from-[#D4AF37]/50 to-transparent" />
                )}
                
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto bg-[#D4AF37] text-black font-bold text-2xl flex items-center justify-center mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase mb-2">{step.title}</h3>
                  <p className="text-white/50 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-4">Success Stories</p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase">
              What Students{' '}
              <span className="text-[#D4AF37]">Say</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((testimonial, idx) => (
              <div key={idx} className="bg-[#0A0A0A] border border-white/5 p-6" data-testid={`testimonial-${idx}`}>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-white/70 mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-white font-medium">{testimonial.name}</p>
                    <p className="text-white/40 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-4">Questions</p>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase">
              Frequently{' '}
              <span className="text-[#D4AF37]">Asked</span>
            </h2>
          </div>

          <div className="space-y-4">
            {FAQ.map((faq, idx) => (
              <div 
                key={idx} 
                className="bg-[#121212] border border-white/5"
                data-testid={`faq-${idx}`}
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <span className="text-white font-medium pr-4">{faq.question}</span>
                  <ChevronRight className={`w-5 h-5 text-[#D4AF37] transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                </button>
                {expandedFaq === idx && (
                  <div className="px-4 pb-4">
                    <p className="text-white/60">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-6">
            Ready to{' '}
            <span className="text-[#D4AF37]">Level Up?</span>
          </h2>
          <p className="text-white/50 text-lg mb-8 max-w-2xl mx-auto">
            Join hundreds of trucking professionals who've transformed their careers with Queen B Academy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handlePurchase('bundle')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8962E] transition-colors"
              data-testid="final-cta-bundle"
            >
              <GraduationCap className="w-5 h-5" />
              Get the Complete Bundle - $800
            </button>
            <Link
              to="/hive/access"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border-2 border-white/20 text-white font-bold uppercase tracking-wider hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              data-testid="final-cta-hive"
            >
              Access The Hive
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Course Detail Modal */}
      {selectedCourse && (
        <CourseDetailModal 
          course={selectedCourse} 
          onClose={() => setSelectedCourse(null)}
          onPurchase={() => handlePurchase('individual', selectedCourse.id)}
        />
      )}
    </div>
  );
};

// Course Detail Modal Component
const CourseDetailModal = ({ course, onClose, onPurchase }) => {
  const IconComponent = ICON_MAP[course.icon] || BookOpen;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="course-detail-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0D0D0D] border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#0D0D0D] border-b border-white/10 p-6 flex items-start justify-between z-10">
          <div className="flex items-start gap-4">
            <div className="p-3" style={{ backgroundColor: `${course.color}15` }}>
              <IconComponent className="w-8 h-8" style={{ color: course.color }} />
            </div>
            <div>
              <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold text-white uppercase">{course.title}</h2>
              <p className="text-white/50">{course.subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white text-2xl">&times;</button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Course Info */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bg-[#121212] p-4">
              <p className="text-white/40 text-sm mb-1">Duration</p>
              <p className="text-white font-bold">{course.duration}</p>
            </div>
            <div className="bg-[#121212] p-4">
              <p className="text-white/40 text-sm mb-1">Level</p>
              <p className="text-white font-bold">{course.level}</p>
            </div>
            <div className="bg-[#121212] p-4">
              <p className="text-white/40 text-sm mb-1">Price</p>
              <p className="text-[#D4AF37] font-bold text-xl">${course.price}</p>
            </div>
          </div>

          {/* Description */}
          <div className="mb-8">
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase mb-4">About This Course</h3>
            <p className="text-white/60">{course.description}</p>
          </div>

          {/* What You'll Learn */}
          <div className="mb-8">
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase mb-4">What You'll Learn</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {course.outcomes.map((outcome, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-white/70">{outcome}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Full Curriculum */}
          <div className="mb-8">
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase mb-4">Full Curriculum</h3>
            <div className="space-y-4">
              {course.modules.map((module, idx) => (
                <div key={module.id} className="bg-[#121212] border border-white/5">
                  <div className="p-4 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      <span className="text-[#D4AF37] font-mono font-bold">Module {idx + 1}</span>
                      <span className="text-white font-medium">{module.title}</span>
                    </div>
                    <p className="text-white/40 text-sm mt-1">{module.description}</p>
                  </div>
                  <div className="p-4 space-y-2">
                    {module.lessons.map((lesson, lessonIdx) => (
                      <div key={lesson.id} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Play className="w-4 h-4 text-white/30" />
                          <span className="text-white/70">{lesson.title}</span>
                        </div>
                        <span className="text-white/40">{lesson.duration}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="mb-8">
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase mb-4">Course Features</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {course.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-[#121212] p-3">
                  <Shield className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-white/70">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="sticky bottom-0 bg-[#0D0D0D] border-t border-white/10 p-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div>
            <p className="text-white/40 text-sm">One-time payment</p>
            <p className="text-2xl font-bold text-[#D4AF37]">${course.price}</p>
          </div>
          <button
            onClick={onPurchase}
            className="w-full sm:w-auto px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8962E] transition-colors"
            data-testid="modal-purchase-btn"
          >
            Purchase This Course
          </button>
        </div>
      </div>
    </div>
  );
};

export default QueenBAcademyPage;
